/**
 * Badge "Ultima integracao Zeus" no header (Task #63 + #87 + #97).
 *
 * #87 trocou a fonte de dado do `/api/zeus/last-sync` (so trades) pra
 * `/api/integracao/zeus/status` (datasets: trades, curva forward,
 * dimensions). #97 adicionou posicao_aberta como 4o dataset (cron
 * zeus_volume_total criado em #96 apos bug VOTENER). Titulo da pill
 * mostra o "elo mais fraco" via `oldest_sync` (timestamp mais antigo
 * entre os 3 crons — dimensions nao puxa porque eh manual). Cor da
 * pill segue `global_status` em verde/ambar/vermelho.
 *
 * Tooltip ao hover lista os 4 datasets com timestamp + status,
 * permitindo Mesa diagnosticar qual dataset esta defasado:
 *   Trades:         30/04 12:19  ●ok
 *   Curva Forward:  30/04 09:00  ●ok
 *   Posicao Aberta: 01/05 07:10  ●ok
 *   Dimensions:     25/04 14:00  ●manual
 *
 * Acessibilidade: tooltip via `title` (mouseover + focus keyboard
 * acionam ambos por default no browser); aria-label resume status
 * global pra screen reader.
 */
import { Activity } from "lucide-react";
import { parseUtcNaive } from "../../utils/dateBr";
import type {
  DatasetStatus,
  ZeusIntegracaoStatusResponse,
} from "../../api/types";

interface Props {
  data: ZeusIntegracaoStatusResponse | null;
  isLoading: boolean;
}

const STATUS_CLASS: Record<ZeusIntegracaoStatusResponse["global_status"], string> = {
  ok: "text-emerald-200/90 bg-emerald-400/10 border border-emerald-300/30",
  stale: "text-amber-200/90 bg-amber-400/10 border border-amber-300/30",
  error: "text-rose-200/90 bg-rose-400/15 border border-rose-300/30",
};

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function fmtDateTime(iso: string): string {
  // F-25a-1: timestamps do backend sao UTC NAIVE — parsear como UTC.
  const d = parseUtcNaive(iso);
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function formatPrincipal(oldest: string | null, status: ZeusIntegracaoStatusResponse["global_status"]): string {
  if (oldest === null) {
    if (status === "error") return "Zeus: erro";
    return "Zeus: ?";
  }
  return `Zeus: ${fmtDateTime(oldest)}`;
}

const DATASET_LABEL: Record<keyof ZeusIntegracaoStatusResponse["datasets"], string> = {
  trades: "Trades",
  curva_forward: "Curva Forward",
  posicao_aberta: "Posição Aberta",
  dimensions: "Dimensions",
};

// Ordem fixa do tooltip: 3 crons primeiro (em ordem de cron schedule
// 07:00 / 07:05 / 07:10 BRT), dimensions por ultimo como referencia
// secundaria.
const TOOLTIP_ORDER: ReadonlyArray<keyof ZeusIntegracaoStatusResponse["datasets"]> = [
  "trades",
  "curva_forward",
  "posicao_aberta",
  "dimensions",
];

function statusMarker(d: DatasetStatus): string {
  // Tipo "manual" sempre eh neutro — Mesa entende que dimensions roda
  // sob demanda, antiguidade nao indica problema.
  if (d.tipo === "manual") return "manual";
  return d.status; // ok | stale | error
}

function buildTooltip(data: ZeusIntegracaoStatusResponse): string {
  const lines: string[] = [];
  for (const key of TOOLTIP_ORDER) {
    const d = data.datasets[key];
    const ts = d.last_sync ? fmtDateTime(d.last_sync) : "—";
    const marker = statusMarker(d);
    lines.push(`${DATASET_LABEL[key]}: ${ts}  •${marker}`);
  }
  return lines.join("\n");
}

function ariaLabelFor(data: ZeusIntegracaoStatusResponse): string {
  const status = data.global_status;
  const oldest = data.oldest_sync ? fmtDateTime(data.oldest_sync) : "sem dados";
  return `Status integracao Zeus: ${status}; ultima atualizacao do elo mais fraco: ${oldest}`;
}

export function ZeusSyncBadge({ data, isLoading }: Props) {
  if (isLoading) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/40 bg-white/5 px-2 py-0.5 rounded border border-white/10"
        data-testid="zeus-sync-badge"
        data-status="loading"
        aria-label="Carregando status da última integração Zeus"
      >
        <Activity size={10} />
        Zeus: …
      </span>
    );
  }

  // data === null aqui significa erro real (fetch falhou) — UI graceful:
  // pill cinza neutra, "Zeus: ?", sem quebrar TopBar.
  if (!data) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-white/40 bg-white/5 px-2 py-0.5 rounded border border-white/10"
        data-testid="zeus-sync-badge"
        data-status="error"
        title="Sem dados de integração Zeus."
        aria-label="Status integração Zeus: sem dados"
      >
        <Activity size={10} />
        Zeus: ?
      </span>
    );
  }

  const status = data.global_status;
  const principal = formatPrincipal(data.oldest_sync, status);
  return (
    <span
      className={
        "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded whitespace-pre-line " +
        STATUS_CLASS[status]
      }
      data-testid="zeus-sync-badge"
      data-status={status}
      title={buildTooltip(data)}
      aria-label={ariaLabelFor(data)}
      tabIndex={0}
    >
      <Activity size={10} />
      {principal}
    </span>
  );
}
